import React, { useEffect, useState } from 'react'
import { Button, Modal, message } from 'antd'
import styled from 'styled-components/macro'
import CreateCustomRoleModal from 'src/screens/settings/CreateCustomRoleModal'
import {
  createVenueCustomRole,
  deleteVenueCustomRole,
  getVenueCustomRolesApiError,
  isCustomRolesPaidPlanError,
  listVenueCustomRoles,
  updateVenueCustomRole,
} from 'src/utils/venueCustomRolesApi'
import { notifyCustomRolesPaywallSales } from 'src/utils/customRolesPaywallNotify'
import {
  CUSTOM_ROLE_ASSIGNED_DELETE_MESSAGE,
  MAX_CUSTOM_ROLES_PER_VENUE,
  canAddCustomRole,
  canDeleteCustomRole,
  getAssignedCountCopy,
  getBasedOnLabel,
  getCustomRoleDeleteBlockedCopy,
  getEnabledCapabilityChips,
  resolveAssignedCount,
} from 'src/utils/venueRoleCapabilities'

const asRoleList = customRoles =>
  Array.isArray(customRoles) ? customRoles : []

const ManageCustomRolesSection = ({
  venueId,
  customRoles = [],
  additionalUsers = [],
  canManage = false,
  isFreePlan = false,
  startCreateToken = 0,
  onCustomRolesChange,
  onFreePlanPaywall,
}) => {
  const [showCreate, setShowCreate] = useState(false)
  const [editingRole, setEditingRole] = useState(null)
  const [roleToDelete, setRoleToDelete] = useState(null)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState('')
  const roles = asRoleList(customRoles)
  const atLimit = !canAddCustomRole(roles)
  const modalOpen = showCreate || Boolean(editingRole)

  const refreshRoles = async fallbackRoles => {
    try {
      const listed = await listVenueCustomRoles(venueId)
      if (Array.isArray(listed?.customRoles)) {
        onCustomRolesChange?.(listed.customRoles)
        return listed.customRoles
      }
    } catch (_listError) {
      // Keep the optimistic list if the follow-up list call fails.
    }
    if (fallbackRoles) onCustomRolesChange?.(fallbackRoles)
    return fallbackRoles || roles
  }

  useEffect(() => {
    if (!venueId) return undefined
    let cancelled = false
    listVenueCustomRoles(venueId)
      .then(listed => {
        if (cancelled || !Array.isArray(listed?.customRoles)) return undefined
        onCustomRolesChange?.(listed.customRoles)
        return listed.customRoles
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [venueId])

  const openUpgradeLock = (payload = {}) => {
    notifyCustomRolesPaywallSales({
      venueId,
      source: 'client',
      platform: 'dashboard',
      roleName: payload.roleName || payload.name || null,
    })
    onFreePlanPaywall?.()
  }

  const openCreate = () => {
    setError('')
    setEditingRole(null)
    if (isFreePlan) {
      openUpgradeLock()
      return
    }
    setShowCreate(true)
  }

  useEffect(() => {
    if (!startCreateToken || !canManage || atLimit) return undefined
    openCreate()
    return undefined
  }, [startCreateToken, canManage, atLimit])

  const closeForm = () => {
    setError('')
    setShowCreate(false)
    setEditingRole(null)
  }

  const handlePaywall = (payload = {}) => {
    closeForm()
    openUpgradeLock({
      roleName: payload.roleName || payload.name || editingRole?.name || null,
    })
  }

  const handleSave = async payload => {
    if (isFreePlan) {
      handlePaywall({ roleName: payload?.name })
      return
    }
    if (!venueId) return
    setSaving(true)
    setError('')
    try {
      if (editingRole?.id) {
        const updated = await updateVenueCustomRole(
          venueId,
          editingRole.id,
          payload,
        )
        const nextRoles = roles.map(role =>
          role.id === editingRole.id ? { ...role, ...updated } : role,
        )
        await refreshRoles(nextRoles)
        message.success(`Saved ${updated?.name || payload.name}`)
      } else {
        const created = await createVenueCustomRole(venueId, payload)
        await refreshRoles(created ? [...roles, created] : roles)
        message.success(`Created ${created?.name || payload.name}`)
      }
      closeForm()
    } catch (saveError) {
      if (isCustomRolesPaidPlanError(saveError)) {
        handlePaywall({ roleName: payload?.name })
        return
      }
      setError(getVenueCustomRolesApiError(saveError))
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteConfirm = async () => {
    if (!venueId || !roleToDelete?.id) return
    setDeleting(true)
    try {
      await deleteVenueCustomRole(venueId, roleToDelete.id)
      const nextRoles = roles.filter(role => role.id !== roleToDelete.id)
      await refreshRoles(nextRoles)
      message.success(`Deleted ${roleToDelete.name}`)
      setRoleToDelete(null)
    } catch (deleteError) {
      message.error(getVenueCustomRolesApiError(deleteError))
    } finally {
      setDeleting(false)
    }
  }

  return (
    <Section>
      <Header>
        <div>
          <Title>Custom roles</Title>
          <Subtitle>
            Name a role, start from Admin, Head Chef / GM, Chef, Order Only, or
            Kitchen Porter, then tick what they can access. You can edit ticks
            at any time.
          </Subtitle>
        </div>
        {canManage ? (
          <Button type='primary' onClick={openCreate} disabled={atLimit}>
            Create custom role
          </Button>
        ) : null}
      </Header>

      {atLimit && canManage ? (
        <LimitNote>
          You can create up to {MAX_CUSTOM_ROLES_PER_VENUE} custom roles on this
          venue.
        </LimitNote>
      ) : null}

      {roles.length === 0 ? (
        <Empty>
          No custom roles yet. Create one when someone needs a named set of
          ticks — for example a Kitchen Supervisor based on Chef who can also
          receipt.
        </Empty>
      ) : (
        <RoleList>
          {roles.map(role => {
            const chips = getEnabledCapabilityChips(role.capabilities)
            const assignedCount = resolveAssignedCount(role, {
              users: additionalUsers,
              venueId,
            })
            const unused = canDeleteCustomRole(role, {
              users: additionalUsers,
              venueId,
            })
            return (
              <RoleCard key={role.id}>
                <RoleName>{role.name}</RoleName>
                <BasedOn>{getBasedOnLabel(role.basedOn)}</BasedOn>
                <Assigned>{getAssignedCountCopy(assignedCount)}</Assigned>
                {chips.length > 0 ? (
                  <Chips>
                    {chips.map(chip => (
                      <Chip key={chip.key}>{chip.label}</Chip>
                    ))}
                  </Chips>
                ) : null}
                {canManage ? (
                  <>
                    <Actions>
                      <Button
                        size='small'
                        onClick={() => {
                          if (isFreePlan) {
                            openUpgradeLock({ roleName: role.name })
                            return
                          }
                          setShowCreate(false)
                          setError('')
                          setEditingRole(role)
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        type='danger'
                        size='small'
                        disabled={!unused}
                        title={
                          unused
                            ? 'Delete this unused role'
                            : CUSTOM_ROLE_ASSIGNED_DELETE_MESSAGE
                        }
                        onClick={() => unused && setRoleToDelete(role)}
                      >
                        Delete
                      </Button>
                    </Actions>
                    {!unused ? (
                      <DeleteBlocked>
                        {getCustomRoleDeleteBlockedCopy(assignedCount)}
                      </DeleteBlocked>
                    ) : null}
                  </>
                ) : null}
              </RoleCard>
            )
          })}
        </RoleList>
      )}

      <CreateCustomRoleModal
        visible={modalOpen}
        role={editingRole}
        existingRoles={roles}
        saving={saving}
        isFreePlan={isFreePlan}
        error={error}
        onCancel={() => {
          if (saving) return
          closeForm()
        }}
        onSave={handleSave}
        onPaywall={handlePaywall}
      />

      <Modal
        title='Delete custom role'
        visible={Boolean(roleToDelete)}
        onOk={handleDeleteConfirm}
        onCancel={() => !deleting && setRoleToDelete(null)}
        confirmLoading={deleting}
        okText='Delete'
        okType='danger'
        okButtonProps={{ disabled: deleting }}
      >
        <p>
          Delete <strong>{roleToDelete?.name}</strong>? Nobody is assigned to
          it.
        </p>
      </Modal>
    </Section>
  )
}

const Section = styled.div`
  margin: 8px 0 24px;
  padding: 16px;
  background: #fafafa;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;
`

const Title = styled.h4`
  margin: 0 0 4px;
  font-size: 16px;
  font-weight: 600;
  color: #262626;
`

const Subtitle = styled.p`
  margin: 0;
  font-size: 13px;
  color: #8c8c8c;
  line-height: 1.4;
`

const LimitNote = styled.p`
  margin: 0 0 12px;
  font-size: 13px;
  color: #9a3412;
`

const Empty = styled.p`
  margin: 0;
  font-size: 13px;
  color: #8c8c8c;
  line-height: 1.5;
`

const RoleList = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const RoleCard = styled.div`
  background: #ffffff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 12px 16px;
`

const RoleName = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: #262626;
`

const BasedOn = styled.div`
  margin-top: 4px;
  font-size: 12px;
  color: #595959;
`

const Assigned = styled.div`
  margin-top: 4px;
  font-size: 12px;
  color: #8c8c8c;
`

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
`

const Chip = styled.span`
  font-size: 11px;
  color: #389e0d;
  background: #f6ffed;
  border: 1px solid #b7eb8f;
  border-radius: 999px;
  padding: 1px 8px;
`

const Actions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
`

const DeleteBlocked = styled.p`
  margin: 8px 0 0;
  font-size: 12px;
  line-height: 1.4;
  color: #a8071a;
`

export default ManageCustomRolesSection
